/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react'
import dayjs from 'dayjs'
import { Fragment, ReactElement } from 'react';
import FeaturedArticlesComponent from './Components/FeaturedArticlesComponent'
import HtmlComponent from './Components/HtmlComponent'
import ImageComponent from './Components/ImageComponent'
import LeafletsComponent from './Components/LeafletsComponent'
import NewsComponent from './Components/NewsComponent'
import ProductsComponent from './Components/ProductsComponent'
import RecipesComponent from './Components/RecipesComponent'
import FontFace, { genJyuuGothicBold } from '../fonts/FontFace'
import { FeaturedArticle, FeaturedArticleComponentProps } from '../types'
import { font300, font600 } from '../utils/styles'

type Props = {
  article: FeaturedArticle
  components: FeaturedArticleComponentProps
}

function Article(props: Props): ReactElement {
  const article = props.article
  const content = article.content
  const components = props.components

  return (
    <Fragment>
      <FontFace />
      <div css={articleStyle}>
        {content.topVisualImage && (
          <img
            src={content.topVisualImage.url}
            css={topVisualImageStyle}
            className="top_visual_image"
          />
        )}
        {(content.lead || content.description || article.publishedAt) && (
          <div css={leadDescriptionWrapperStyle}>
            {content.lead && (
              <h2 css={leadStyle(content.leadTextColor)} className="lead">
                {content.lead.split(/\n/).map((leadElement, i) => (
                  <Fragment key={i}>
                    {leadElement}
                    <br />
                  </Fragment>
                ))}
              </h2>
            )}
            {content.description && (
              <p css={descriptionStyle} className="description">
                {content.description.split(/\n/).map((descriptionElement, i) => (
                  <Fragment key={i}>
                    {descriptionElement}
                    <br />
                  </Fragment>
                ))}
              </p>
            )}
            {article.publishedAt && (
              <div css={publishedAtStyle} className="published_at">
                公開日：{dayjs(article.publishedAt).format('YYYY年M月D日')}
              </div>
            )}
          </div>
        )}
        {components.map((component, i) => {
          if (component.key === 'featured_articles_component') {
            return <FeaturedArticlesComponent component={component} key={i} />
          } else if (component.key === 'html_component') {
            return <HtmlComponent component={component} key={i} />
          } else if (component.key === 'image_component') {
            return <ImageComponent component={component} key={i} />
          } else if (component.key === 'leaflets_component') {
            return <LeafletsComponent component={component} key={i} />
          } else if (component.key === 'news_component') {
            return <NewsComponent component={component} key={i} />
          } else if (component.key === 'recipes_component') {
            return <RecipesComponent component={component} key={i} />
          } else if (component.key === 'products_component') {
            return <ProductsComponent component={component} key={i} />
          }
        })}
      </div>
    </Fragment>
  );
}

const articleStyle = css`
  font-family: 'ヒラギノ角ゴ Pro W3', 'Hiragino Kaku Gothic Pro', 'メイリオ', Meiryo,
    'ＭＳ Ｐゴシック', sans-serif;
  overflow-x: hidden;
`
const topVisualImageStyle = css`
  display: block;
  width: 100%;
`

const leadDescriptionWrapperStyle = css`
  padding: 32px;
`

const leadStyle = (leadTextColor: string) => css`
  ${genJyuuGothicBold}
  font-size: 20px;
  color: ${leadTextColor};
  text-align: center;
  margin-top: 0;
  line-height: 30px;
`

const descriptionStyle = css`
  ${font300}
  margin: 0 0 5px 0;
  line-height: 180%;
`

const publishedAtStyle = css`
  ${font600}
  margin: 0;
  line-height: 180%;
  color: rgba(0, 0, 0, 0.54);
  text-align: right;
`

export default Article
